import { useState, useRef, useEffect } from "react";
import { FieldInputProps } from "formik";
import Select, { components } from "react-select";

interface DropdownCellProps extends FieldInputProps<any> {
  options: { value: string | number; label: string }[];
  label?: string;
  disabled?: boolean;
  disableSort?: boolean;
  hasError?: boolean;
}

const useContainerSize = () => {
  const [size, setSize] = useState({ width: 0, height: 0 });
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setSize({ width, height });
    });

    resizeObserver.observe(ref.current);
    return () => resizeObserver.disconnect();
  }, []);

  return { ref, size };
};

const sortOptions = (options: { value: string | number; label: string }[]) => {
  return [...options].sort((a, b) => a.label.localeCompare(b.label));
};

const CheckIcon = ({ containerWidth }: { containerWidth: number }) => {
  const isSmall = containerWidth <= 200;
  const isExtraSmall = containerWidth <= 115;

  const size = isExtraSmall ? "w-2 h-2" : isSmall ? "w-3 h-3" : "w-4 h-4";

  return (
    <svg
      className={`${size} stroke-green-500`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
};

const CustomDropdownIndicator = (props: any) => {
  const { containerWidth } = props;
  const isExtraSmall = containerWidth <= 115;

  if (isExtraSmall) {
    return null;
  }

  return (
    <components.DropdownIndicator {...props}>
      <svg
        width="10"
        height="16"
        viewBox="4 6 12 8"
        fill="currentColor"
        style={{ color: "#6B7280" }}
      >
        <path
          fillRule="evenodd"
          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
          clipRule="evenodd"
        />
      </svg>
    </components.DropdownIndicator>
  );
};

const CustomOption = (props: any) => {
  const { data, isSelected, containerWidth } = props;
  return (
    <components.Option {...props}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {data.label}
        {isSelected && <CheckIcon containerWidth={containerWidth} />}
      </div>
    </components.Option>
  );
};

const CustomSingleValue = (props: any) => {
  const { containerWidth } = props;
  return (
    <components.SingleValue {...props}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          gap: "0",
        }}
      >
        <span
          style={{
            flex: "1",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "left",
          }}
        >
          {props.data.label}
        </span>
      </div>
    </components.SingleValue>
  );
};

const customStyles = (hasError?: boolean, containerWidth?: number) => {
  const isSmall = containerWidth && containerWidth <= 200;
  const isExtraSmall = containerWidth && containerWidth <= 115;

  const fontSize = isExtraSmall ? "0.5rem" : isSmall ? "0.825rem" : "0.75rem";
  const controlPadding = isExtraSmall
    ? "0.125rem"
    : isSmall
    ? ".05rem"
    : "0.5rem";
  const optionPadding = isExtraSmall ? "4px 6px" : isSmall ? "6px 8px" : "10px";

  return {
    control: (base: any, state: any) => ({
      ...base,
      width: "100%",
      paddingTop: controlPadding,
      paddingBottom: controlPadding,
      paddingLeft: isExtraSmall ? "0.125rem" : "0.25rem",
      paddingRight: isExtraSmall ? "0.125rem" : "0.25rem",
      border: "none",
      borderRadius: "0.5rem",
      backgroundColor: "#f3f4f6",
      transition: "border-color 0.2s, box-shadow 0.2s",
      boxShadow: "none",
      fontSize,
      minHeight: isExtraSmall ? "24px" : isSmall ? "32px" : "auto",
      "&:hover": {
        borderColor: "#6366F1",
      },
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: isExtraSmall ? "0 2px" : isSmall ? "0 4px" : "0 8px",
      flex: "1 1 0%",
      width: "100%",
    }),
    singleValue: (base: any) => ({
      ...base,
      color: "#374151",
      fontSize,
      width: "100%",
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }),
    placeholder: (base: any) => ({
      ...base,
      color: hasError ? "#EF4444" : "#9CA3AF",
      fontSize,
      width: "100%",
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }),
    input: (base: any) => ({
      ...base,
      width: "100%",
      maxWidth: "100%",
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      display: isExtraSmall ? "none" : "flex",
      flexShrink: 0,
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      padding: isExtraSmall ? "0" : "2px",
    }),

    indicatorSeparator: (base: any) => ({
      ...base,
      display: isExtraSmall ? "none" : "block",
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: "white",
      borderRadius: "0.5rem",
      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.4)",
      padding: "2px",
      zIndex: 9999,
      fontSize,
    }),
    menuPortal: (base: any) => ({
      ...base,
      zIndex: 9999,
    }),
    option: (base: any, { isSelected }: any) => ({
      ...base,
      backgroundColor: isSelected ? "#f3f4f6" : "white",
      color: "#374151",
      padding: optionPadding,
      borderRadius: "0.25rem",
      fontSize,
      "&:hover": {
        backgroundColor: "#e5e7eb",
      },
    }),
  };
};

const SelectDropdownCell = ({
  name,
  value,
  onBlur,
  onChange,
  label,
  options,
  disabled,
  disableSort = false,
  hasError,
}: DropdownCellProps) => {
  const { ref, size } = useContainerSize();
  const sortedOptions = disableSort ? options : sortOptions(options);
  const selectedOption =
    sortedOptions.find((option) => option.value === value) || null;
  const isExtraSmall = size.width <= 115;
  const isSmall = size.width <= 200;

  return (
    <div ref={ref}>
      {label && (
        <label
          htmlFor={name}
          className="block text-xs text-black mb-1 font-bold"
          style={{
            fontSize: isExtraSmall
              ? "text-xs"
              : isSmall
              ? "text-sm"
              : "text-md",
          }}
        >
          {label}
        </label>
      )}
      <Select
        name={name}
        isDisabled={disabled}
        options={sortedOptions}
        value={selectedOption}
        onChange={(selectedOption) => {
          onChange({
            target: { name, value: selectedOption ? selectedOption.value : "" },
          });
        }}
        onBlur={onBlur}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            onBlur(e as unknown as React.FocusEvent<HTMLInputElement>);
          }
        }}
        isSearchable={false}
        placeholder={hasError ? "*Required" : "Select..."}
        components={{
          Option: (props) => (
            <CustomOption {...props} containerWidth={size.width} />
          ),
          SingleValue: (props) => (
            <CustomSingleValue {...props} containerWidth={size.width} />
          ),
          DropdownIndicator: (props) => (
            <CustomDropdownIndicator {...props} containerWidth={size.width} />
          ),
        }}
        styles={customStyles(hasError, size.width)}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        menuPlacement="auto"
      />
    </div>
  );
};

const SearchSelectDropdownCell: React.FC<DropdownCellProps> = ({
  name,
  value,
  onBlur,
  onChange,
  options,
  label,
  disabled,
  disableSort = false,
  hasError,
}) => {
  const { ref, size } = useContainerSize();
  const sortedOptions = disableSort ? options : sortOptions(options);
  const selectedOption =
    sortedOptions.find((option) => option.value === value) || null;
  const isExtraSmall = size.width <= 115;
  const isSmall = size.width <= 200;

  return (
    <div ref={ref}>
      {label && (
        <label
          htmlFor={name}
          className="block text-sm text-black mb-1 font-bold"
          style={{
            fontSize: isExtraSmall
              ? "text-xs"
              : isSmall
              ? "text-sm"
              : "text-md",
          }}
        >
          {label}
        </label>
      )}
      <Select
        name={name}
        isDisabled={disabled}
        options={sortedOptions}
        value={selectedOption}
        onChange={(selectedOption) => {
          onChange({
            target: {
              name,
              value: selectedOption ? selectedOption.value : null,
            },
          });
        }}
        onBlur={onBlur}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            onBlur(e as unknown as React.FocusEvent<HTMLInputElement>);
          }
        }}
        isSearchable={true}
        placeholder={hasError ? "*Required" : "Search..."}
        components={{
          Option: (props) => (
            <CustomOption {...props} containerWidth={size.width} />
          ),
          SingleValue: (props) => (
            <CustomSingleValue {...props} containerWidth={size.width} />
          ),
          DropdownIndicator: (props) => (
            <CustomDropdownIndicator {...props} containerWidth={size.width} />
          ),
        }}
        styles={customStyles(hasError, size.width)}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        menuPlacement="auto"
      />
    </div>
  );
};

const SelectCell = ({ options, hasError, ...rest }: DropdownCellProps) => {
  return (
    <div className="dropdown w-full">
      {options.length > 6 ? (
        <SearchSelectDropdownCell
          options={options}
          hasError={hasError}
          {...rest}
        />
      ) : (
        <SelectDropdownCell options={options} hasError={hasError} {...rest} />
      )}
    </div>
  );
};

export default SelectCell;
